import {
	Box,
	Chip,
	Grid2,
	LinearProgress,
	Skeleton,
	Typography,
} from "@mui/material";
import { withFraction32 } from "@/utils/fraction";
import { OverviewLiveSignals, OverviewProductRun } from "../models/types";
import { microLabelSx } from "../styles";

interface Props {
	live: OverviewLiveSignals | null;
	productRuns: OverviewProductRun[];
	loading?: boolean;
}

// Role label is fixed; Part # / Extended Part # get the most room, Clip #
// enough for "Clip part NN", the single-number columns the least.
const GRID_COLS = "60px 2fr 1.9fr 0.85fr 1.3fr 0.55fr 0.75fr 0.85fr";

const ProductSummarySkeleton = () => (
	<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
			<Skeleton variant="text" width={160} height={22} />
			<Box sx={{ flexGrow: 1 }} />
			<Skeleton variant="rounded" width={60} height={20} />
		</Box>
		<Box
			sx={{
				border: "1px solid #e0e0e0",
				borderRadius: 1,
				backgroundColor: "#fff",
			}}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: GRID_COLS,
					columnGap: 0.5,
					py: 0.4,
					px: 0.5,
					borderBottom: "1px solid #e0e0e0",
				}}
			>
				<Box />
				{Array.from({ length: 7 }).map((_, i) => (
					<Skeleton key={i} variant="text" width="80%" sx={{ mx: "auto" }} />
				))}
			</Box>
			{Array.from({ length: 3 }).map((_, r) => (
				<Box
					key={r}
					sx={{
						display: "grid",
						gridTemplateColumns: GRID_COLS,
						columnGap: 0.5,
						py: 0.5,
						px: 0.5,
					}}
				>
					<Skeleton variant="text" width={50} />
					{Array.from({ length: 7 }).map((_, c) => (
						<Skeleton key={c} variant="text" width="60%" sx={{ mx: "auto" }} />
					))}
				</Box>
			))}
		</Box>
	</Box>
);

const fmtInt = (n: number | null | undefined) =>
	n === null || n === undefined ? "-" : Math.round(n).toString();

const fmtProgress = (
	current: number | null | undefined,
	total: number | null | undefined,
) =>
	current == null || total == null || total <= 0
		? "-"
		: `${fmtInt(current)} / ${fmtInt(total)}`;

type ChipTone = "good" | "attention" | "unknown";

const TONE_COLORS: Record<
	ChipTone,
	{ bg: string; border: string; text: string; dot: string }
> = {
	good: { bg: "#e8f5e9", border: "#4CAF50", text: "#2e7d32", dot: "#4CAF50" },
	attention: {
		bg: "#fff3e0",
		border: "#FB8C00",
		text: "#e65100",
		dot: "#FB8C00",
	},
	unknown: { bg: "#fafafa", border: "#e0e0e0", text: "#999", dot: "#bdbdbd" },
};

const StateChip = ({ label, tone }: { label: string; tone: ChipTone }) => {
	const c = TONE_COLORS[tone];
	return (
		<Chip
			size="small"
			label={
				<Box
					component="span"
					sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
				>
					<Box
						component="span"
						sx={{
							display: "inline-block",
							width: 7,
							height: 7,
							borderRadius: "50%",
							backgroundColor: c.dot,
						}}
					/>
					{label}
				</Box>
			}
			sx={{
				height: 20,
				fontSize: "0.65rem",
				fontWeight: 700,
				letterSpacing: 0.3,
				backgroundColor: c.bg,
				color: c.text,
				border: `1px solid ${c.border}`,
				"& .MuiChip-label": { px: 0.7 },
			}}
		/>
	);
};

const HEADERS = [
	"Part #",
	"Extended Part #",
	"Length",
	"Clip #",
	"Weld Program",
	"Pieces Welded",
	"Pieces Remaining",
] as const;

type Row = {
	role: "Previous" | "Current" | "Next";
	values: (string | number | null | undefined)[];
};

const TableHeader = () => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: GRID_COLS,
			alignItems: "baseline",
			columnGap: 0.4,
			py: 0.4,
			px: 0.4,
			borderBottom: "1px solid #e0e0e0",
		}}
	>
		<Box />
		{HEADERS.map((h) => (
			<Typography
				key={h}
				sx={{ ...microLabelSx, textAlign: "center", lineHeight: 1.15 }}
			>
				{h}
			</Typography>
		))}
	</Box>
);

type LoadedRowState = "delay" | "cycling" | "ready";

const LOADED_ROW_COLORS: Record<LoadedRowState, { bg: string; text: string }> =
	{
		delay: { bg: "#fff8e1", text: "#e65100" },
		cycling: { bg: "#f1f8e9", text: "#1b5e20" },
		ready: { bg: "#f5f5f5", text: "#555" },
	};

const TableRow = ({
	row,
	loadedRowState,
}: {
	row: Row;
	loadedRowState: LoadedRowState;
}) => {
	const isLoaded = row.role === "Current";
	const loadedColors = LOADED_ROW_COLORS[loadedRowState];
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: GRID_COLS,
				alignItems: "center",
				columnGap: 0.4,
				py: 0.45,
				px: 0.4,
				backgroundColor: isLoaded ? loadedColors.bg : "transparent",
				borderRadius: isLoaded ? 0.5 : 0,
			}}
		>
			<Typography
				sx={{
					fontSize: "0.7rem",
					fontStyle: isLoaded ? "normal" : "italic",
					color: isLoaded ? loadedColors.text : "#888",
					fontWeight: isLoaded ? 700 : 500,
					textTransform: "uppercase",
					letterSpacing: 0.3,
				}}
			>
				{row.role}
			</Typography>
			{row.values.map((v, idx) => (
				<Typography
					key={`${row.role}-${idx}`}
					sx={{
						fontSize: isLoaded ? "0.85rem" : "0.78rem",
						fontWeight: isLoaded ? 700 : 500,
						color:
							v === null || v === undefined || v === ""
								? "#bbb"
								: isLoaded
									? loadedColors.text
									: "#555",
						textAlign: "center",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{v === null || v === undefined || v === "" ? "-" : v}
				</Typography>
			))}
		</Box>
	);
};

const BundleStat = ({ label, value }: { label: string; value: string }) => (
	<Box>
		<Typography sx={microLabelSx}>{label}</Typography>
		<Typography
			sx={{
				fontSize: "0.9rem",
				fontWeight: 700,
				color: value === "-" ? "#bbb" : "#333",
				fontVariantNumeric: "tabular-nums",
			}}
		>
			{value}
		</Typography>
	</Box>
);

export const ProductSummaryCard = ({ live, productRuns, loading }: Props) => {
	if (loading) return <ProductSummarySkeleton />;
	const loadedRowState: LoadedRowState =
		live?.IsInDelay === 1
			? "delay"
			: live?.CycleInProgress === 1
				? "cycling"
				: "ready";

	// Treat the bundle as "active" when ACC counters > 0 or a cycle is running;
	// otherwise counters and the weld program are stale from a previous bundle.
	const hasActiveBundle =
		!!live &&
		((live.BarsPerLayerAcc ?? 0) > 0 ||
			(live.LayersPerBundleAcc ?? 0) > 0 ||
			live.CycleInProgress === 1);

	const barsCell =
		hasActiveBundle && live?.BarsPerLayerPre
			? fmtProgress(live.BarsPerLayerAcc, live.BarsPerLayerPre)
			: null;
	const layersCell =
		hasActiveBundle && live?.LayersPerBundlePre
			? fmtProgress(live.LayersPerBundleAcc, live.LayersPerBundlePre)
			: null;
	const piecesPerBundleCell =
		hasActiveBundle && live?.PiecesPerBundle != null
			? fmtInt(live.PiecesPerBundle)
			: "-";
	const piecesWeldedCell =
		hasActiveBundle && live?.PiecesWelded != null
			? fmtInt(live.PiecesWelded)
			: "-";
	const piecesRemainingCell =
		hasActiveBundle && live?.PiecesRemaining != null
			? fmtInt(live.PiecesRemaining)
			: "-";
	const bundleProgressPct =
		hasActiveBundle &&
		live?.PiecesWelded != null &&
		live?.PiecesPerBundle != null &&
		live.PiecesPerBundle > 0
			? Math.min(100, (live.PiecesWelded / live.PiecesPerBundle) * 100)
			: 0;
	const bundleLoadingLabel =
		live?.BundleOnLoading === null || live?.BundleOnLoading === undefined
			? "-"
			: live.BundleOnLoading === 1
				? "Yes"
				: "No";

	const runForRole = (role: Row["role"]): OverviewProductRun | null =>
		productRuns.find((r) => r.Role === role) ?? null;

	const buildRow = (role: Row["role"]): Row => {
		const run = runForRole(role);
		return {
			role,
			values: [
				run?.PartNumber ?? null,
				run?.ExtendedPartNumber ?? null,
				run?.Length != null ? withFraction32(run.Length) : null,
				run?.ClipNumber ?? null,
				run?.WeldProgram ?? null,
				run?.PiecesWelded ?? null,
				run?.PiecesRemaining ?? null,
			],
		};
	};

	const rows: Row[] = [
		buildRow("Previous"),
		buildRow("Current"),
		buildRow("Next"),
	];

	const currentPartNumber = runForRole("Current")?.PartNumber ?? null;
	const currentNotes = runForRole("Current")?.Notes?.trim() || null;

	const autoLabel = live?.InAuto === 1 ? "AUTO" : "MANUAL";
	const autoTone: ChipTone =
		live?.InAuto === null || live?.InAuto === undefined
			? "unknown"
			: live.InAuto === 1
				? "good"
				: "attention";
	const cycleLabel = live?.CycleInProgress === 1 ? "ACTIVE" : "IDLE";
	const cycleTone: ChipTone =
		live?.CycleInProgress === null || live?.CycleInProgress === undefined
			? "unknown"
			: live.CycleInProgress === 1
				? "good"
				: "unknown";

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					mb: 0.5,
					flexWrap: "wrap",
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1 }}>
					Current Product:{" "}
					{currentPartNumber ? (
						<Box component="span" sx={{ fontWeight: 700, color: "#1b5e20" }}>
							{currentPartNumber}
						</Box>
					) : (
						<Box component="span" sx={{ color: "#bbb", fontWeight: 600 }}>
							—
						</Box>
					)}
				</Typography>
				<Box sx={{ flexGrow: 1 }} />
				<StateChip label={autoLabel} tone={autoTone} />
				<StateChip label={cycleLabel} tone={cycleTone} />
			</Box>

			<Box
				sx={{
					border: "1px solid #e0e0e0",
					borderRadius: 1,
					backgroundColor: "#fff",
				}}
			>
				<TableHeader />
				{rows.map((r) => (
					<TableRow key={r.role} row={r} loadedRowState={loadedRowState} />
				))}
			</Box>

			{currentNotes && (
				<Box
					sx={{
						mt: 0.75,
						px: 1,
						py: 0.5,
						backgroundColor: "#fff8e1",
						border: "1px solid #ffe082",
						borderRadius: 1,
						display: "flex",
						gap: 0.75,
						alignItems: "flex-start",
					}}
				>
					<Typography
						sx={{
							fontSize: "0.62rem",
							fontWeight: 700,
							color: "#8d6e63",
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							pt: "2px",
							flexShrink: 0,
						}}
					>
						WO Note
					</Typography>
					<Typography sx={{ fontSize: "0.78rem", color: "#5d4037" }}>
						{currentNotes}
					</Typography>
				</Box>
			)}

			<Box sx={{ mt: "auto", pt: 1, borderTop: "1px solid #e0e0e0" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						mb: 0.75,
					}}
				>
					<Typography sx={microLabelSx}>Bundle progress</Typography>
					<Chip
						size="small"
						label={`Loading: ${bundleLoadingLabel}`}
						sx={{
							height: 18,
							fontSize: "0.6rem",
							fontWeight: 700,
							color: live?.BundleOnLoading === 1 ? "#2e7d32" : "#666",
							backgroundColor:
								live?.BundleOnLoading === 1 ? "#e8f5e9" : "#fafafa",
							"& .MuiChip-label": { px: 0.75 },
						}}
					/>
				</Box>

				<Box
					sx={{
						border: "1px solid #e0e0e0",
						borderRadius: 1,
						backgroundColor: "#fff",
						p: 1,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "baseline",
							justifyContent: "space-between",
							gap: 1,
						}}
					>
						<Box>
							<Typography sx={microLabelSx}>Welded</Typography>
							<Typography
								sx={{
									fontSize: "1.05rem",
									fontWeight: 800,
									color: hasActiveBundle ? "#2e7d32" : "#bbb",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{piecesWeldedCell}
							</Typography>
						</Box>

						<Box sx={{ textAlign: "right" }}>
							<Typography sx={microLabelSx}>Remaining</Typography>
							<Typography
								sx={{
									fontSize: "1.05rem",
									fontWeight: 800,
									color: hasActiveBundle ? "#e65100" : "#bbb",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{piecesRemainingCell}
							</Typography>
						</Box>
					</Box>
					<LinearProgress
						color="success"
						variant="determinate"
						value={bundleProgressPct}
						sx={{ mt: 0.75, height: 7, borderRadius: 1 }}
					/>
				</Box>

				<Grid2 container spacing={1} sx={{ mt: 0.75 }}>
					<Grid2 size={{ xs: 4 }}>
						<BundleStat label="Bars / layer" value={barsCell ?? "-"} />
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<BundleStat label="Layers / bundle" value={layersCell ?? "-"} />
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<BundleStat label="Pieces / bundle" value={piecesPerBundleCell} />
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
};
